/**
 * Sessions Table Component
 * Displays the list of sessions in a table format
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Edit2, Trash2, ChevronLeft, ChevronRight, AlertCircle, Target, Eye
} from 'lucide-react';

// Format date helper
const formatDate = (isoString) => {
  if (!isoString) return 'N/A';
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return isoString.split('T')[0];
  }
};

const SessionsTable = ({ 
  sessions, 
  total, 
  page, 
  totalPages, 
  onPageChange,
  onEdit,
  onDelete,
  onViewDetails 
}) => {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle>Sessions ({total})</CardTitle>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            Page {page} of {totalPages}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {sessions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No sessions found</p>
            <p className="text-sm">Try adjusting your filters</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Date & Time</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Child</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Observer</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Duration</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Amount</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Status</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">View</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session, idx) => (
                    <tr 
                      key={session.id || idx} 
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => onViewDetails?.(session)}
                    >
                      <td className="p-3">
                        <div className="text-sm font-medium">{formatDate(session.session_date || session.started_at)}</div>
                        <div className="text-xs text-gray-500">
                          {session.started_at?.split('T')[1]?.substring(0, 5) || 'N/A'} - {session.ended_at?.split('T')[1]?.substring(0, 5) || 'N/A'}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-600">
                            {session.child_name?.charAt(0) || '?'}
                          </div>
                          <span className="text-sm">{session.child_name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-xs font-bold text-purple-600">
                            {session.observer_name?.charAt(0) || '?'}
                          </div>
                          <span className="text-sm">{session.observer_name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="text-sm">{session.duration_minutes || 0} min</span>
                      </td>
                      <td className="p-3">
                        <span className="text-sm font-medium text-green-600">₹{session.earnings || 0}</span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          session.status === 'completed' ? 'bg-green-100 text-green-700' :
                          session.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          session.status === 'deleted' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {session.status || 'unknown'}
                        </span>
                        {session.is_manual_entry && (
                          <span className="ml-1 px-1 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">manual</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); onViewDetails?.(session); }}
                            className="text-sanjaya-navy hover:text-sanjaya-navy/80 hover:bg-sanjaya-navy/10"
                            title="View Full Analysis"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); onEdit(session); }}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); onDelete(session.id); }}
                            className="text-red-600 hover:text-red-800"
                            disabled={session.status === 'deleted'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="text-sm text-gray-500">
                Showing {((page - 1) * 50) + 1} - {Math.min(page * 50, total)} of {total}
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => onPageChange(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                >
                  Next <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SessionsTable;
